import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { cn } from './cn';
import { t } from '../../lib/i18n';

/**
 * Yazarak arayan, listede olmayan değeri de kabul eden açılır liste
 * ("creatable select"). Departman ve Rol alanları bunu kullanıyor: hazır liste
 * var ama kullanıcı kendi rolünü yazabiliyor.
 *
 * Liste pencerenin (dialog) kaydırma kutusuna sıkışmasın diye gövdeye portal
 * ile çiziliyor ve tetikleyicinin konumuna sabitleniyor.
 */

export interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxProps {
  value: string;
  options: ReadonlyArray<ComboboxOption>;
  onChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  id?: string;
  disabled?: boolean;
  className?: string;
  'aria-labelledby'?: string;
}

export function Combobox({
  value,
  options,
  onChange,
  placeholder,
  searchPlaceholder,
  id,
  disabled,
  className,
  ...aria
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const selectedLabel = options.find((option) => option.value === value)?.label ?? value;

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('tr');
    if (!needle) return options;
    return options.filter((option) => option.label.toLocaleLowerCase('tr').includes(needle));
  }, [options, query]);

  /** Listede tam karşılığı olmayan bir metin yazıldıysa "ekle" satırı çıkar. */
  const custom = query.trim();
  const canCreate =
    custom.length > 0 &&
    !options.some((option) => option.label.toLocaleLowerCase('tr') === custom.toLocaleLowerCase('tr'));
  const rows: Array<ComboboxOption & { isNew?: boolean }> = canCreate
    ? [...filtered, { value: custom, label: custom, isNew: true }]
    : [...filtered];

  const place = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setAnchor({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  };

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !listRef.current?.contains(target)) setOpen(false);
    };
    const reposition = () => place();
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', reposition);
    // Arkadaki sayfa kaydırılırsa liste tetikleyiciyle birlikte hareket etsin.
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  const openList = () => {
    setQuery('');
    setActiveIndex(Math.max(0, options.findIndex((option) => option.value === value)));
    setOpen(true);
  };

  const choose = (index: number) => {
    const row = rows[index];
    setOpen(false);
    triggerRef.current?.focus();
    if (row && row.value !== value) onChange(row.value);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        openList();
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((index) => Math.min(rows.length - 1, index + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((index) => Math.max(0, index - 1));
        break;
      case 'Enter':
        event.preventDefault();
        choose(activeIndex);
        break;
      case 'Escape':
        event.preventDefault();
        // Pencere içinde açıldıysa Escape yalnızca listeyi kapatsın.
        event.stopPropagation();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-field px-3 py-2 text-sm shadow-xs outline-none',
          'transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/30',
          'disabled:cursor-not-allowed disabled:opacity-50',
          value ? 'text-foreground' : 'text-muted-foreground',
          className
        )}
        {...aria}
      >
        <span className="truncate">{value ? selectedLabel : placeholder}</span>
        <ChevronDown className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>

      {open &&
        anchor &&
        createPortal(
          <div
            ref={listRef}
            style={{ position: 'fixed', top: anchor.top, left: anchor.left, width: anchor.width }}
            className="animate-popover-in z-[60] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
          >
            <input
              autoFocus
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              className="mb-1 h-8 w-full rounded-sm bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground"
            />
            <ul id={listId} role="listbox" className="max-h-56 overflow-y-auto">
              {rows.length === 0 && (
                <li className="px-2 py-2 text-sm text-muted-foreground">{t('Sonuç yok.', 'No results.')}</li>
              )}
              {rows.map((row, index) => (
                <li
                  key={`${row.value}-${index}`}
                  role="option"
                  aria-selected={row.value === value}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(index)}
                  className={cn(
                    'relative flex cursor-default select-none items-center gap-2 rounded-sm py-1.5 pl-2 pr-8 text-sm',
                    index === activeIndex && 'bg-accent text-accent-foreground'
                  )}
                >
                  {row.isNew && <Plus className="size-3.5 shrink-0 text-muted-foreground" />}
                  <span className="truncate">
                    {row.isNew ? t(`"${row.label}" ekle`, `Add "${row.label}"`) : row.label}
                  </span>
                  {row.value === value && <Check className="absolute right-2 size-4 text-brand" />}
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
    </>
  );
}
